import React, { useState, useEffect, useMemo } from "react";
import { FileDown, Eye } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import ScoutingReportManager from "../../managers/ScoutingReportsManager";
import { fetchAPI, fetchAPIBlob } from "../../config/api";
import { toDivisionApi } from "../../config/constants";
import { computeColorRanges } from "../../components/baserunning-report";
import { BaseballTable } from "../../components/tables/BaseballTable";
import { columnsBaserunningPlayer } from "../../config/leaderboards";
import { InsightCard } from "./components/insight-card";
import { ReportHeader } from "../../components/reports";

const r1 = (v) => (v != null ? Number(v).toFixed(1) : "—");

export const BaserunningReportPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [headshotMap, setHeadshotMap] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const reportData = await ScoutingReportManager.getReportById(reportId);
        setReport(reportData);
        if (!reportData) return;

        const playerIds = (reportData.positionPlayers || []).map((p) => p.id).filter(Boolean);
        if (!playerIds.length) return;

        const division = toDivisionApi(reportData.division);
        const year = reportData.year;
        const params = new URLSearchParams({ division, start_year: year, end_year: year });

        const [data, bulkPlayers] = await Promise.all([
          fetchAPI(`/leaderboards/baserunning?${params}`),
          fetchAPI("/players/bulk", {
            method: "POST",
            body: JSON.stringify({ player_ids: playerIds }),
          }),
        ]);

        setAllRows(data || []);
        setFilteredRows((data || []).filter((r) => playerIds.includes(r.player_id)));

        const map = {};
        Object.entries(bulkPlayers || {}).forEach(([pid, info]) => {
          if (info?.headshot_url) map[pid] = info.headshot_url;
        });
        setHeadshotMap(map);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (reportId) load();
  }, [reportId]);

  const colorRanges = useMemo(() => computeColorRanges(allRows), [allRows]);
  const columns = useMemo(
    () => columnsBaserunningPlayer.filter((c) => c.name !== "#"),
    []
  );

  const insights = useMemo(() => {
    if (!filteredRows.length) return null;
    const top = (key) =>
      [...filteredRows].sort((a, b) => (b[key] ?? -99) - (a[key] ?? -99))[0];
    const topBsr = top("baserunning");
    const mostSb = top("sb");
    const bestSbPct =
      filteredRows.filter((r) => (r.sb_att ?? 0) >= 3)
        .sort((a, b) => (b.sb_pct ?? 0) - (a.sb_pct ?? 0))[0] ?? null;
    const mostAggressive = [...filteredRows].sort(
      (a, b) => ((b.ebt_taken ?? 0) + (b.ebt_out ?? 0)) - ((a.ebt_taken ?? 0) + (a.ebt_out ?? 0))
    )[0];
    const topWsb = top("wsb");
    return { topBsr, mostSb, bestSbPct, mostAggressive, topWsb };
  }, [filteredRows]);

  const handlePreview = () => {
    if (!filteredRows.length) return;
    sessionStorage.setItem("baserunningConfig", JSON.stringify({
      rows: filteredRows,
      colorRanges,
      report: { teamName: report.teamName, year: report.year, teamId: report.teamId },
    }));
    window.open("/render/baserunning-report", "_blank");
  };

  const handleExport = async () => {
    if (exporting || !report) return;
    setExporting(true);
    try {
      const blob = await fetchAPIBlob("/reports/baserunning_report", {
        method: "POST",
        body: JSON.stringify({
          rows: filteredRows,
          colorRanges,
          report: { teamName: report.teamName, year: report.year, teamId: report.teamId },
        }),
      });
      saveAs(blob, `${(report.teamName || "team").replace(/\s+/g, "_")}_baserunning_${report.year}.pdf`);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Report Not Found</h2>
          <button onClick={() => navigate("/scouting")} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Back to Scouting
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <ReportHeader
          teamId={report.teamId}
          teamName={report.teamName}
          reportType="Baserunning"
          year={report.year}
        >
          <button
            onClick={handlePreview}
            disabled={filteredRows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Eye size={14} />
            Preview
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || filteredRows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileDown size={14} />
            {exporting ? "Exporting…" : "Export PDF"}
          </button>
        </ReportHeader>

        {insights && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-6">
            <InsightCard
              label="Best Baserunner"
              player={insights.topBsr}
              value={r1(insights.topBsr?.baserunning)}
              unit="BsR"
              headshotUrl={headshotMap[insights.topBsr?.player_id]}
            />
            <InsightCard
              label="SB Leader"
              player={insights.mostSb}
              value={insights.mostSb?.sb ?? "—"}
              unit="Stolen Bases"
              headshotUrl={headshotMap[insights.mostSb?.player_id]}
            />
    
            <InsightCard
              label="Most Aggressive"
              player={insights.mostAggressive}
              value={(insights.mostAggressive?.ebt_taken ?? 0) + (insights.mostAggressive?.ebt_out ?? 0)}
              unit="XBT Attempts"
              headshotUrl={headshotMap[insights.mostAggressive?.player_id]}
            />
            <InsightCard
              label="wSB Leader"
              player={insights.topWsb}
              value={r1(insights.topWsb?.wsb)}
              unit="Weighted Stolen Bases"
              headshotUrl={headshotMap[insights.topWsb?.player_id]}
            />
          </div>
        )}

        <BaseballTable
          data={filteredRows}
          columns={columns}
          filename={`${(report.teamName || "team").replace(/\s+/g, "_")}_baserunning_${report.year}.csv`}
          defaultSortField={columns.findIndex((c) => c.name === "BsR") + 1}
        />
      </div>
    </div>
  );
};
